/**
 * #577 (d-20260924-03) — the household's sound and haptic preferences, read from and saved to
 * `GET`/`PATCH /api/tenants/current`.
 *
 * Five booleans, all off until a parent chooses. `kioskSound` is a permission for the shared
 * display, not a sound of its own. Only a parent's save is accepted by the server, and this module
 * never decides who may save: it reports what the server answered.
 *
 * `read` and `write` are passed in so a test can steer one request without mocking a module.
 */
import { fetchTenant, updateTenant } from './session.js';

export const SOUND_KEYS = Object.freeze(['completionChime', 'paydayFanfare', 'villageAmbience', 'kioskSound', 'softHaptic']);

/**
 * The five preferences from a family object, or `null` when any one of them is not a real boolean.
 * A partial answer (an older server, a trimmed payload) must read as "not known", never as five
 * confident `false` values a parent would take for their saved choice.
 */
export function toSoundPreferences(tenant) {
  if (!tenant || typeof tenant !== 'object' || Array.isArray(tenant)) return null;
  const preferences = {};
  for (const key of SOUND_KEYS) {
    if (typeof tenant[key] !== 'boolean') return null;
    preferences[key] = tenant[key];
  }
  return preferences;
}

/** @throws when the server cannot be reached or its answer cannot be read. */
export async function fetchSoundPreferences({ read = fetchTenant } = {}) {
  const preferences = toSoundPreferences(await read());
  if (!preferences) throw new Error('The family answered without its sound preferences');
  return preferences;
}

/**
 * Save the switches that changed, and answer with a receipt built from what the SERVER saved.
 *
 * Only the changed keys are sent, so a save never re-asserts a switch the parent did not touch.
 *
 * @param {string} familyId the tenant id the provider is bound to
 * @param {object} next the full map the parent asked for
 * @param {{write?: Function, previous?: object}} options
 * @returns {Promise<{receiptId: string, status: 'saved', familyId: string, preferences: object}>}
 */
export async function saveSoundPreferences(familyId, next, { write = updateTenant, previous = {} } = {}) {
  const patch = {};
  for (const key of SOUND_KEYS) {
    if (typeof next?.[key] === 'boolean' && next[key] !== previous?.[key]) patch[key] = next[key];
  }
  if (Object.keys(patch).length === 0) throw new Error('No sound preference changed');
  const tenant = await write(patch);
  const preferences = toSoundPreferences(tenant);
  if (!preferences || tenant.id !== familyId) {
    throw new Error('The server did not confirm this family\'s sound preferences');
  }
  return { receiptId: `sound:${tenant.id}:${Date.now()}`, status: 'saved', familyId: tenant.id, preferences };
}

/** The shape `FeedbackProvider` calls: `(familyId, next, previous)`. */
export function saveSoundPreferencesFromProvider(familyId, next, previous) {
  return saveSoundPreferences(familyId, next, { previous });
}
